import styled from 'styled-components';
import axios from 'axios';
import NPLStatsService from '../../service/nplStats/NPLStats';
import { useState, useEffect, useMemo, useCallback } from 'react';
import type { RankItem } from './components/NPL/NPLRank';
import type { WeeklyTrendItem } from './components/NPL/WeeklyNPLTrend';
import type { RegionMetricsItem as NPLRegionMetricsItem } from './components/NPL/RegionalNPLOverview';
import type { NPLTrendRow } from './components/graph/NPL/NPLTrendAnalysisGraph';
import type { MonthlyTrend } from './components/NPL/NationalNPLDelta';
import { format, endOfISOWeek, startOfISOWeek, addWeeks } from 'date-fns';
import NPLSummaryCards from './components/NPL/NPLSummaryCards';
import NPLRank from './components/NPL/NPLRank';
import WeeklyNPLTrend from './components/NPL/WeeklyNPLTrend';
import NPLUsageOverview from './components/NPL/NPLUsageOverview';
import RegionalNPLOverview from './components/NPL/RegionalNPLOverview';
import NPLTrendsAnalysis from './components/NPL/NPLTrendAnalysis';
import NPLCommonFilter from './components/filter/NPL/NPLCommonFilter';
import NationalNPLDelta from './components/NPL/NationalNPLDelta';
import AnnualNationalNPLTrend from './components/graph/NPL/AnnualNationalNPLTrend';
import PageTop from '../components/pageTop/PageTop';
import { PageContainer } from '../components/common/PageComponent.style';
import PremiumOnlyAlert from './components/PremiumOnlyAlert';
import { useUser } from '../../context/UserContext';

type UsageOverviewItem = {
  id: string;
  usage: string;
  claimAmount: number; // 청구금액 합/평균(백엔드 정의에 따름)
  bidRate: number; // 낙찰률(%)
  winPriceRate: number; // 낙찰가율(%)
  collateralValueRate: number; // 담보가치율(%)
  recoveryRate: number; // 회수율(%)
  lossRate: number; // 손실률(%)
  mortgageAmount: number; // 근저당액
  shortfallRate: number;
};

const NPLStats = () => {
  const [analysisPeriod, setAnalysisPeriod] = useState<
    'week' | 'month' | 'year'
  >('month');

  const [showPremium, setShowPremium] = useState(false);
  const { user } = useUser();

  const toYMD = (d: Date) => format(d, 'yyyy-MM-dd');
  const today = new Date();
  const prevMonthFirst = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  const [date, setDate] = useState<{ from: Date; to: Date }>({
    from: prevMonthFirst,
    to: prevMonthEnd,
  });
  const [filterType, setFilterType] = useState('court');
  const [court, setCourt] = useState('all');
  const [regionCode, setRegionCode] = useState<string>(''); // API용
  const [regionName, setRegionName] = useState<string>('전체'); // 표시/하위 컴포넌트용
  const [usage, setUsage] = useState('전체');
  const [usageDetail, setUsageDetail] = useState('전체');
  const [fiMain, setFiMain] = useState<string>(''); // 금융기관 대분류
  const [fiSub, setFiSub] = useState<string>('금융기관 전체'); // 금융기관 소분류
  const [isDetail, setIsDetail] = useState<boolean>(false); // 상세 용도 전환 여부

  const normalizeAll = (v?: string) =>
    v === 'all' || v === '전체' ? '' : (v ?? '');

  const regionParam = normalizeAll(regionCode);
  const usageParam = normalizeAll(usage);
  const usageDetailParam = normalizeAll(usageDetail);
  const effectiveUsageParam = usageDetailParam || usageParam;
  const courtParam = normalizeAll(court);
  const fiMainParam = normalizeAll(fiMain);
  const fiSubParam = normalizeAll(fiSub);
  const [rankType, setRankType] = useState<'A' | 'B' | 'C' | 'D' | 'E'>('A');

  const [rankData, setRankData] = useState<RankItem[]>([]);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [weeklyTrend, setWeeklyTrend] = useState<WeeklyTrendItem[]>([]);
  const [weeklyLoading, setWeeklyLoading] = useState<boolean>(false);
  const [weeklyError, setWeeklyError] = useState<string | null>(null);

  const [regionalStats, setRegionalStats] = useState<NPLRegionMetricsItem[]>(
    [],
  );
  const [regionalLoading, setRegionalLoading] = useState(false);
  const [regionalError, setRegionalError] = useState<string | null>(null);

  const [nplTrendRows, setNplTrendRows] = useState<NPLTrendRow[]>([]);
  const [nplTrendLoading, setNplTrendLoading] = useState(false);
  const [nplTrendError, setNplTrendError] = useState<string | null>(null);

  const [monthlyTrendData, setMonthlyTrendData] = useState<MonthlyTrend[]>([]);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyError, setMonthlyError] = useState<string | null>(null);

  const [usageRows, setUsageRows] = useState<any[]>([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);

  const [rankingRaw, setRankingRaw] = useState<any[]>([]);
  const [insightText, setInsightText] = useState<string>('');
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError] = useState<string | null>(null);

  const [monthlyInsight, setMonthlyInsight] = useState<any>(null);
  const [monthlyInsightLoading, setMonthlyInsightLoading] = useState(false);
  const [monthlyInsightError, setMonthlyInsightError] = useState<string | null>(
    null,
  );

  function getMonthWeekNumber(date: Date): number {
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const dayOfWeek = firstDayOfMonth.getDay(); // 0:일 ~ 6:토
    const offsetDate = date.getDate() + dayOfWeek - 1;
    return Math.ceil(offsetDate / 7);
  }

  const periodMonthLabel = useMemo(() => {
    const d = date.from;
    return `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
  }, [date.from]);

  const periodWeekLabel = useMemo(() => {
    const d = date.from;
    return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${getMonthWeekNumber(d)}주차`;
  }, [date.from]);

  const periodSummary =
    analysisPeriod === 'week' ? periodWeekLabel : periodMonthLabel;

  const toSummaryDataFromWeek = (it?: WeeklyTrendItem) =>
    !it
      ? null
      : {
          bid_rate: it.bidRate,
          bid_price_rate: it.winPriceRate,
          claim_price: it.claimAmount,
          collateral_value_rate: it.collateralValueRate,
          recovery_rate: it.recoveryRate,
          loss_rate: it.lossRate,
          mortgage_amount: it.mortgageAmount ?? 0,
          shortfall_rate: it.shortfallRate ?? 0,
        };

  const toSummaryDataFromMonth = (it?: MonthlyTrend) =>
    !it
      ? null
      : {
          bid_rate: it.bid_rate,
          bid_price_rate: it.bid_price_rate,
          claim_price: it.claim_price,
          collateral_value_rate: it.collateral_value_rate,
          recovery_rate: it.recovery_rate,
          loss_rate: it.loss_rate,
          mortgage_amount: it.mortgage_amount ?? 0,
          shortfall_rate: it.shortfall_rate ?? 0,
        };

  const summaryWeekData = useMemo(() => {
    if (!weeklyTrend || weeklyTrend.length === 0) return null;
    const last = weeklyTrend[weeklyTrend.length - 1];
    return toSummaryDataFromWeek(last);
  }, [weeklyTrend]);

  const summaryMonthData = useMemo(() => {
    if (!monthlyTrendData || monthlyTrendData.length === 0) return null;
    const last = monthlyTrendData[monthlyTrendData.length - 1];
    return toSummaryDataFromMonth(last);
  }, [monthlyTrendData]);

  const mapNPLRankRows = (rows: any[]): RankItem[] =>
    rows.map((row: any, idx: number) => ({
      // 중복 방지용 id(사건번호+인덱스)
      id: `${row.casenumber ?? ''}#${idx}`,
      caseNo: String(row.casenumber ?? '').trim(),
      address: String(row.location ?? '').trim(),
      usage: String(row.use ?? '').trim(),
      claimAmount: row.claim_price, // 청구금액
      winningBidAmount: row.bid_price, // 낙찰가
      winningPriceRate: row.bid_price_rate, // 낙찰가율 %
      collateralValueRate: row.collateral_value_rate, // 담보가치율 %
      recoveryRate: row.recovery_rate, // 회수율 %
      mortgageAmount: row.mortgage_amount,
      lossRate: row.loss_rate,
      shortfallRate: row.recall_shortage_rate,
    }));

  const toNum = (v: any, fb = 0) => {
    const n = Number(String(v ?? '').replace(/[,%\s]/g, ''));
    return Number.isFinite(n) ? n : fb;
  };

  const toISO = (v: any) => {
    if (!v) return '';
    // 서버가 "YYYY-MM" 또는 "YYYY-MM-DD" 둘 다 낼 수 있다고 가정
    const s = String(v);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    if (/^\d{4}-\d{2}$/.test(s)) return `${s}-01`;
    return '';
  };

  const mapWeeklyTrendRows = (rows: any[]) =>
    rows.map((r: any, idx: number) => ({
      id: `${r.week ?? r.period ?? idx}#${idx}`,
      week: String(r.week ?? '').trim(),
      period: String(r.period ?? '').trim(),
      claimAmount: toNum(r.claim_price),
      bidRate: toNum(r.bid_rate),
      winPriceRate: toNum(r.bid_price_rate),
      collateralValueRate: toNum(r.collateral_value_rate),
      recoveryRate: toNum(r.recovery_rate),
      lossRate: toNum(r.loss_rate),
      mortgageAmount: toNum(r.mortgage_amount),
      shortfallRate: toNum(r.recall_shortage_rate),
    }));

  // 랭킹 조회
  const fetchRank = useCallback(async () => {
    setRankingLoading(true);
    try {
      const res = await NPLStatsService.nplRank(
        rankType,
        courtParam,
        regionParam,
        effectiveUsageParam,
        fiSubParam,
        toYMD(date.from),
        toYMD(date.to),
        30,
      );

      // Axios 기준: res.data => { status, data: [...] }
      // fetch 등 자유형식까지 커버: 배열이면 바로 사용
      const raw = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];

      setRankingRaw(raw);
      setRankData(mapNPLRankRows(raw));
    } catch (e) {
      console.error(e);
      setRankData([]);
    } finally {
      setRankingLoading(false);
    }
  }, [
    rankType,
    courtParam,
    regionParam,
    effectiveUsageParam,
    fiMainParam,
    fiSubParam,
    date.from,
    date.to,
  ]);

  const fetchWeeklyTrend = useCallback(async () => {
    setWeeklyLoading(true);
    setWeeklyError(null);
    try {
      // weeklyTrend는 단일 date 쿼리를 받으므로 주의: 보통 주의 "끝일" 또는 "속한 날짜"를 넘깁니다.
      const targetDate = toYMD(date.to); // 혹은 date.from
      const res = await NPLStatsService.weeklyTrend(
        courtParam,
        regionParam,
        effectiveUsageParam,
        fiSubParam, // <- 일관되게 사용
        targetDate,
      );

      const raw = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];

      setWeeklyTrend(mapWeeklyTrendRows(raw));
    } catch (e: any) {
      console.error(e);
      setWeeklyTrend([]);
      setWeeklyError(e?.message ?? '주간 데이터 로딩 실패');
    } finally {
      setWeeklyLoading(false);
    }
  }, [courtParam, regionParam, effectiveUsageParam, fiSubParam, date.to]);

  const mapUsageOverviewRows = (rows: any[]): UsageOverviewItem[] =>
    rows.map((r: any, idx: number) => ({
      id: `${idx}-${String(r.usage ?? '').trim()}`,
      usage: String(r.usage ?? '').trim(),
      claimAmount: toNum(r.claim_price),
      bidRate: toNum(r.bid_rate),
      winPriceRate: toNum(r.bid_price_rate),
      collateralValueRate: toNum(r.collateral_value_rate),
      recoveryRate: toNum(r.recovery_rate),
      lossRate: toNum(r.loss_rate),
      mortgageAmount: toNum(r.mortgage_amount),
      shortfallRate: toNum(r.recall_shortage_rate),
    }));

  const fetchUsageOverview = useCallback(async () => {
    setUsageLoading(true);
    setUsageError(null);
    try {
      const res = await NPLStatsService.usageOverview(
        courtParam,
        regionParam,
        effectiveUsageParam,
        fiSubParam,
        toYMD(date.from),
        toYMD(date.to),
        isDetail,
      );

      const raw = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];

      setUsageRows(mapUsageOverviewRows(raw));
    } catch (e: any) {
      console.error(e);
      setUsageRows([]);
      setUsageError(e?.message ?? '용도별 지표 로딩 실패');
    } finally {
      setUsageLoading(false);
    }
  }, [
    courtParam,
    regionParam,
    effectiveUsageParam,
    fiSubParam,
    date.from,
    date.to,
    isDetail,
  ]);

  const mapRegionalOverviewRows = (rows: any[]) =>
    rows.map((r: any, idx: number) => ({
      id: `${idx}-${String(r.sido ?? '').trim()}`,
      region: String(r.sido ?? '').trim(),
      claimAmount: toNum(r.claim_price),
      bidRate: toNum(r.bid_rate),
      winPriceRate: toNum(r.bid_price_rate),
      collateralValueRate: toNum(r.collateral_value_rate),
      recoveryRate: toNum(r.recovery_rate),
      lossRate: toNum(r.loss_rate), // 음수 가능 (예: 서울)
      mortgageAmount: toNum(r.mortgage_amount),
      shortfallRate: toNum(r.recall_shortage_rate),
    }));

  const fetchRegionalOverview = useCallback(async () => {
    setRegionalLoading(true);
    setRegionalError(null);
    try {
      const res = await NPLStatsService.regionalOverview(
        courtParam,
        regionParam,
        effectiveUsageParam,
        fiSubParam,
        toYMD(date.from),
        toYMD(date.to),
      );

      // 서비스가 result.data를 반환하므로 여기서는 res가 보통 {status, data} 또는 배열일 수 있음
      const raw = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : Array.isArray(res?.data?.rows)
              ? res.data.rows
              : [];

      setRegionalStats(mapRegionalOverviewRows(raw));
    } catch (e: any) {
      console.error(e);
      setRegionalStats([]);
      setRegionalError(e?.message ?? '지역별 NPL 지표 로딩 실패');
    } finally {
      setRegionalLoading(false);
    }
  }, [
    courtParam,
    regionParam,
    effectiveUsageParam,
    fiSubParam,
    date.from,
    date.to,
  ]);

  const mapMonthlyTrendRows = (rows: any[]): MonthlyTrend[] =>
    rows.map((r: any) => {
      // 필드 이름 방어적으로 처리(startDate|start|from / endDate|end|to)
      const startISO = toISO(r.startDate ?? r.start ?? r.from);
      const endISO = toISO(r.endDate ?? r.end ?? r.to);

      return {
        startDate: startISO,
        endDate: endISO,
        claim_price: toNum(r.claim_price),
        bid_rate: toNum(r.bid_rate),
        bid_price_rate: toNum(r.bid_price_rate),
        collateral_value_rate: toNum(r.collateral_value_rate),
        recovery_rate: toNum(r.recovery_rate),
        loss_rate: toNum(r.loss_rate),
        mortgage_amount: toNum(r.mortgage_amount),
        shortfall_rate: toNum(r.recall_shortage_rate),
      };
    });

  // (2) 정렬 + 마지막 두 개만 [이전, 현재]로 만들기
  const sortAndPickPrevCurr = (rows: MonthlyTrend[]): MonthlyTrend[] => {
    const sorted = [...rows].sort((a, b) => {
      // endDate 기준 오름차순
      const ax = new Date(a.endDate ?? a.startDate ?? '').getTime();
      const bx = new Date(b.endDate ?? b.startDate ?? '').getTime();
      return ax - bx;
    });
    const last2 = sorted.slice(-2); // [prev, curr]가 되도록
    // 만약 1개 이하라면 그대로 반환(컴포넌트가 방어됨)
    return last2;
  };

  // (3) fetch 함수
  const fetchMonthlyTrend = useCallback(async () => {
    if (analysisPeriod !== 'month') return;
    setMonthlyLoading(true);
    setMonthlyError(null);
    try {
      const res = await NPLStatsService.monthlyTrend(
        courtParam,
        regionParam,
        effectiveUsageParam,
        fiSubParam,
        toYMD(date.from),
        toYMD(date.to),
      );

      // req 래퍼 구조 방어(배열/객체 둘 다 커버)
      const raw = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];

      const mapped = mapMonthlyTrendRows(raw);
      const picked = sortAndPickPrevCurr(mapped); // [prev, curr] 형태로 맞추기
      setMonthlyTrendData(picked);
    } catch (e: any) {
      console.error(e);
      setMonthlyTrendData([]);
      setMonthlyError(e?.message ?? '월간 NPL 동향 로딩 실패');
    } finally {
      setMonthlyLoading(false);
    }
  }, [
    analysisPeriod,
    courtParam,
    regionParam,
    effectiveUsageParam,
    fiSubParam,
    date.from,
    date.to,
  ]);

  const pad2 = (n: number) => String(n).padStart(2, '0');
  const mmddDot = (mmddSlash: string) => {
    const [m, d] = String(mmddSlash).split('/');
    return `${pad2(Number(m))}.${pad2(Number(d))}`;
  };

  // fetch 함수
  const fetchNplTrend = useCallback(async () => {
    setNplTrendLoading(true);
    setNplTrendError(null);
    try {
      const res = await NPLStatsService.nplTrend(
        analysisPeriod, // 'week' | 'month' | 'year'
        courtParam, // 법원
        regionParam, // 지역
        effectiveUsageParam, // 용도(상세 우선)
        fiSubParam, // 은행(대/소)
        toYMD(date.to), // 기준일
      );

      const rows: any[] = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];

      const mapped: NPLTrendRow[] = rows.map((r: any, idx: number) => {
        // 주차 라벨: "07/21~07/27" → "~07.27"
        // 월 라벨: "2025-02" → "2025.02"
        let label = String(idx);
        if (analysisPeriod === 'week') {
          const periodStr = String(r.period ?? '');
          label = periodStr.includes('~')
            ? `~${mmddDot(periodStr.split('~')[1]?.trim() ?? '')}`
            : r.week
              ? String(r.week)
              : label;
        } else if (analysisPeriod === 'month') {
          const ym = String(r.period ?? r.month ?? r.endDate ?? '');
          label = ym.replace('-', '.').slice(0, 7); // YYYY.MM
        }

        const toNum = (v: any) =>
          Number(String(v ?? '').replace(/[,\s]/g, '')) || 0;

        return {
          label,
          mortgageAmount: toNum(r.mortgage_amount),
          claimPrice: toNum(r.claim_price),
          winRate: toNum(r.bid_rate),
          winPriceRate: toNum(r.bid_price_rate),
          collateralValueRate: toNum(r.collateral_value_rate),
          recoveryRate: toNum(r.recovery_rate),
          lossRate: toNum(r.loss_rate),
          shortfallRate: toNum(r.recall_shortage_rate),
        };
      });

      setNplTrendRows(mapped);
    } catch (e: any) {
      console.error(e);
      setNplTrendRows([]);
      setNplTrendError(e?.message ?? 'NPL 동향 로딩 실패');
    } finally {
      setNplTrendLoading(false);
    }
  }, [
    analysisPeriod,
    courtParam,
    regionParam,
    effectiveUsageParam,
    fiSubParam,
    date.to,
  ]);

  useEffect(() => {
    fetchRank();
  }, [fetchRank, analysisPeriod]);

  useEffect(() => {
    if (analysisPeriod === 'week') {
      fetchWeeklyTrend();
    }
  }, [analysisPeriod, fetchWeeklyTrend]);

  useEffect(() => {
    fetchUsageOverview();
  }, [
    fetchUsageOverview,
    analysisPeriod,
    courtParam,
    regionParam,
    effectiveUsageParam,
    fiSubParam,
    date.from,
    date.to,
    isDetail,
  ]);

  useEffect(() => {
    fetchRegionalOverview();
  }, [fetchRegionalOverview, analysisPeriod]);

  useEffect(() => {
    fetchMonthlyTrend();
  }, [
    fetchMonthlyTrend,
    analysisPeriod,
    courtParam,
    regionParam,
    effectiveUsageParam,
    fiSubParam,
    date.from,
    date.to,
  ]);

  useEffect(() => {
    fetchNplTrend();
  }, [fetchNplTrend]);

  useEffect(() => {
    if (!rankingRaw || rankingRaw.length === 0) {
      setInsightText('');
      return;
    }

    setInsightLoading(true);
    setInsightError(null);

    axios
      .post(
        `${process.env.REACT_APP_HOST_API_STATS}zipgo/ranking/npl-insight`,
        {
          data: rankingRaw, // 가공 전 원본 그대로
          type: rankType, // AuctionRank에서 쓰는 type
          limit: 30,
        },
      )
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : (data?.data ?? []);
        const paragraphs = (arr ?? [])
          .map((it: any) => {
            const emoji = String(it?.emoji ?? '').trim();
            const text = String(it?.text ?? '')
              .replace(/\s*\n+\s*/g, ' ')
              .trim();
            // 이모지 + 공백 + 텍스트 (같은 줄)
            return [emoji, text].filter(Boolean).join(' ');
          })
          .filter(Boolean);

        setInsightText(paragraphs.join('\n\n'));
      })
      .catch((e) => {
        console.error(e);
        setInsightText('');
        setInsightError('랭킹 인사이트를 불러오지 못했어요.');
      })
      .finally(() => setInsightLoading(false));
  }, [date, regionCode, usage, usageDetail, court, rankingRaw, rankType]);

  useEffect(() => {
    // 월간 모드가 아니거나, 원본이 비어있으면 스킵
    if (analysisPeriod !== 'month') return;
    if (!Array.isArray(monthlyTrendData) || monthlyTrendData.length === 0)
      return;

    let cancelled = false;
    setMonthlyInsightLoading(true);
    setMonthlyInsightError(null);

    axios
      .post(
        `${process.env.REACT_APP_HOST_API_STATS}zipgo/monthly-trend/npl-insight`,
        {
          data: monthlyTrendData,
        },
      )
      .then(({ data }) => {
        if (cancelled) return;
        // 응답 형태 콘솔 확인
        const rows = Array.isArray(data) ? data : (data?.data ?? data ?? []);
        setMonthlyInsight(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[monthly-trend/insight] fetch error:', err);
      })
      .finally(() => {
        if (cancelled) return;
        setMonthlyInsightLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [analysisPeriod, monthlyTrendData]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const token = url.searchParams.get('token');

    if (token || user?.plan_grade === 'P') {
      setShowPremium(false);
    } else {
      setShowPremium(true);
    }
  }, [user]);

  useEffect(() => {
    if (showPremium) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [showPremium]);

  useEffect(() => {
    if (!showPremium) return;

    // 현재 스크롤 위치 기억
    const scrollY = window.scrollY || window.pageYOffset;

    // 레이아웃 흔들림 방지용 (선택)
    document.documentElement.style.scrollbarGutter = 'stable';

    // body를 fixed로 고정해 배경 스크롤 차단
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflowY: body.style.overflowY,
    };

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.overflowY = 'scroll'; // 스크롤바 폭 유지로 레이아웃 점프 방지

    return () => {
      // 원복
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.overflowY = prev.overflowY;

      // 원래 위치로 복귀
      window.scrollTo(0, scrollY);
    };
  }, [showPremium]);
  return (
    <PageContainer $bg="#F4F5F7">
      <PremiumOnlyAlert
        open={showPremium}
        onClose={() => {
          /* noop */
        }}
      />
      <PageTop text="NPL 통계" hasSearch={false} />
      <TabBox>
        <Tab
          $active={analysisPeriod === 'month'}
          onClick={() => {
            setAnalysisPeriod('month');
            const now = new Date();
            const prevFirst = new Date(
              now.getFullYear(),
              now.getMonth() - 1,
              1,
            );
            const prevEnd = new Date(now.getFullYear(), now.getMonth(), 0);
            setDate({ from: prevFirst, to: prevEnd }); // 전월로 즉시 갱신
          }}
          aria-selected={analysisPeriod === 'month'}
          role="tab"
        >
          월간 NPL 통계
        </Tab>
        <Tab
          $active={analysisPeriod === 'week'}
          onClick={() => {
            setAnalysisPeriod('week');
            const now = new Date();
            const lastWeekStart = startOfISOWeek(addWeeks(now, -1));
            const lastWeekEnd = endOfISOWeek(addWeeks(now, -1));
            setDate({ from: lastWeekStart, to: lastWeekEnd });
          }}
        >
          주간 NPL 통계
        </Tab>
      </TabBox>

      <Section>
        <NPLCommonFilter
          analysisPeriod={analysisPeriod}
          setAnalysisPeriod={setAnalysisPeriod}
          date={date}
          setDate={setDate}
          filterType={filterType}
          setFilterType={setFilterType}
          court={court}
          setCourt={setCourt}
          regionCode={regionCode}
          setRegionCode={setRegionCode}
          regionName={regionName}
          setRegionName={setRegionName}
          usage={usage}
          setUsage={setUsage}
          usageDetail={usageDetail}
          setUsageDetail={setUsageDetail}
          fiMain={fiMain}
          setFiMain={setFiMain}
          fiSub={fiSub}
          setFiSub={setFiSub}
        />

        <NPLSummaryCards
          court={court}
          region={regionName}
          usage={usage}
          usageDetail={usageDetail}
          date={date}
          type={analysisPeriod}
          summaryData={
            analysisPeriod === 'week' ? summaryWeekData : summaryMonthData
          }
          isLoading={analysisPeriod === 'week' ? weeklyLoading : monthlyLoading}
        />
      </Section>

      <Content>
        {analysisPeriod === 'month' && (
          <NationalNPLDelta
            data={monthlyTrendData}
            loading={monthlyLoading}
            error={monthlyError}
            periodType={'month'}
            insights={monthlyInsight}
            insightsLoading={monthlyInsightLoading}
            insightsError={monthlyInsightError}
          />
        )}
        {/* 주요 경매 물건 순위 */}
        <NPLRank
          periodSummary={periodSummary}
          region={regionName}
          usage={usage}
          usageDetail={usageDetail}
          court={court}
          fiMain={fiMain}
          fiSub={fiSub}
          type={analysisPeriod}
          selectedType={rankType}
          onTypeChange={setRankType}
          data={rankData}
          loading={rankingLoading}
          insights={insightText}
          insightsLoading={insightLoading}
          insightsError={insightError}
        />
        {analysisPeriod === 'week' && (
          // 주간 경매 동향
          <WeeklyNPLTrend
            periodSummary={periodSummary}
            region={regionName}
            court={court}
            usage={usage}
            usageDetail={usageDetail}
            fiMain={fiMain}
            fiSub={fiSub}
            data={weeklyTrend}
            loading={weeklyLoading}
            error={weeklyError}
          />
        )}
        {/* 용도별 경매 지표 현황 */}
        <NPLUsageOverview
          periodSummary={periodSummary}
          region={regionName}
          isDetail={isDetail}
          onToggleDetail={() => setIsDetail((v) => !v)}
          usage={usage}
          usageDetail={usageDetail}
          court={court}
          fiMain={fiMain}
          fiSub={fiSub}
          usageData={usageRows}
          loading={usageLoading}
          error={usageError}
        />
        {/* 지역별 경매 지표 현황 */}
        <RegionalNPLOverview
          periodSummary={periodSummary}
          region={regionName}
          usage={usage}
          usageDetail={usageDetail}
          fiMain={fiMain}
          fiSub={fiSub}
          data={regionalStats}
          loading={regionalLoading}
          error={regionalError}
        />
        {/* 경매 동향 분석 */}
        <NPLTrendsAnalysis
          region={regionName}
          usage={usage}
          usageDetail={usageDetail}
          court={court}
          fiMain={fiMain}
          fiSub={fiSub}
          data={nplTrendRows}
          loading={nplTrendLoading}
          error={nplTrendError}
        />
        {/* 연간 전국 경매 지표 동향 */}
        <AnnualNationalNPLTrend
          region={regionName}
          usage={usage}
          usageDetail={usageDetail}
          court={court}
          fiMain={fiMain}
          fiSub={fiSub}
          type={analysisPeriod}
          data={nplTrendRows}
          loading={nplTrendLoading}
          error={nplTrendError}
        />
      </Content>
    </PageContainer>
  );
};

export default NPLStats;

const TabBox = styled.div`
  display: flex;
  align-items: flex-start;
  flex: 1 0 0;
  border-bottom: 1px solid #e5e8ea;
  background: #fff;

  position: sticky;
  top: 56px;
  z-index: 10;
`;

const Tab = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  outline: none;

  display: flex;
  padding: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex: 1 0 0;
  align-self: stretch;
  background: #fff;
  color: #787d87;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;

  ${({ $active }) =>
    $active &&
    `
  border-bottom: 2px solid #0c0e12;
  color: #0C0E12;
  
`}
`;

const Section = styled.div`
  display: flex;
  padding: 16px 16px 24px 16px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;
